/**
 * Frontend "Is Similar To" / "Is Not Similar To" Operator.
 *
 * Registers with the module registry to handle the `fuzzy_is` and
 * `fuzzy_isnot` operators using Levenshtein distance fuzzy matching.
 *
 * @package
 */

import { registerModule } from './module-registry';

/**
 * Compute the Levenshtein distance between two strings.
 *
 * @param a First string.
 * @param b Second string.
 * @return The edit distance between the two strings.
 */
function levenshtein(a: string, b: string): number {
	const an = a.length;
	const bn = b.length;

	if (an === 0) {
		return bn;
	}
	if (bn === 0) {
		return an;
	}

	// Create a matrix of distances.
	const matrix: number[][] = [];

	for (let i = 0; i <= bn; i++) {
		matrix[i] = [i];
	}

	for (let j = 0; j <= an; j++) {
		matrix[0][j] = j;
	}

	for (let i = 1; i <= bn; i++) {
		for (let j = 1; j <= an; j++) {
			if (b.charAt(i - 1) === a.charAt(j - 1)) {
				matrix[i][j] = matrix[i - 1][j - 1];
			} else {
				matrix[i][j] = Math.min(
					matrix[i - 1][j - 1] + 1, // substitution
					matrix[i][j - 1] + 1, // insertion
					matrix[i - 1][j] + 1 // deletion
				);
			}
		}
	}

	return matrix[bn][an];
}

/**
 * Normalize a string for fuzzy comparison.
 *
 * Removes diacritics via Unicode NFD decomposition, converts to
 * lowercase, and strips non-alphanumeric characters.
 *
 * @param str The string to normalize.
 * @return Normalized string.
 */
function normalizeString(str: string): string {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');
}

/**
 * Check if two strings are a fuzzy match within the given threshold.
 *
 * @param input     The input string (field value).
 * @param target    The target string (rule value).
 * @param threshold Maximum allowed Levenshtein distance.
 * @return True if the strings are within the threshold distance.
 */
function fuzzyMatch(input: string, target: string, threshold: number): boolean {
	const normalizedInput = normalizeString(input);
	const normalizedTarget = normalizeString(target);
	const distance = levenshtein(normalizedInput, normalizedTarget);
	return distance <= threshold;
}

// Register with the module registry on import.
registerModule({
	slug: 'operator-similar-to',
	handles: (rule) =>
		rule.operator === 'fuzzy_is' || rule.operator === 'fuzzy_isnot',
	evaluate: (isMatch, formId, rule) => {
		// Per-rule threshold override; falls back to the default of 1.
		// The rule object at runtime carries extra GPACL properties beyond
		// the narrow FrontendModule type.
		const ruleAny = rule as Record<string, unknown>;
		const threshold =
			ruleAny.fuzzyThreshold !== undefined &&
			typeof ruleAny.fuzzyThreshold === 'number'
				? ruleAny.fuzzyThreshold
				: 1;

		// Get the source field's value from the DOM.
		const source = jQuery('#input_' + formId + '_' + rule.fieldId);
		if (source.length) {
			const sourceValue = source.val() as string;
			const fuzzyResult = fuzzyMatch(
				sourceValue || '',
				rule.value || '',
				threshold
			);
			return rule.operator === 'fuzzy_isnot' ? !fuzzyResult : fuzzyResult;
		}

		return isMatch;
	},
});
